/**
 * ThreadForm.tsx
 *
 * Purpose: Reusable form component for creating new threads or posting replies.
 * Created: 2026-03-11
 * Author: ralph-manager (Sonnet code loop)
 *
 * WHAT: Form UI for "New Thread" (title + content) or "Reply" (content only) modes.
 * WHY: DRY form logic shared between thread creation and reply workflows.
 * HOW: `mode` prop toggles which fields appear and drives submit label copy.
 *      Parent passes `onSubmit` handler; this component manages local field state.
 * WHERE: Used inside ThreadList (new thread) and ThreadDetail (reply).
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Text,
  VStack,
  HStack,
} from '@chakra-ui/react';

// =============================================================================
// TYPES
// =============================================================================

/** Payload sent to the parent's onSubmit handler. */
export interface ThreadFormPayload {
  /** Thread title — only present when mode is "newThread" */
  title?: string;
  /** Post / reply content */
  content: string;
}

export interface ThreadFormProps {
  /**
   * Determines which fields appear:
   * - "newThread": title input + content textarea (for creating a new thread)
   * - "reply": content textarea only (for adding a reply to an existing thread)
   */
  mode: 'newThread' | 'reply';
  /**
   * Async submit handler called by this form with the validated payload.
   * Parent is responsible for the actual API call.
   * Throwing here surfaces an error message inside the form.
   */
  onSubmit: (data: ThreadFormPayload) => Promise<void>;
  /** Called when the user clicks Cancel. */
  onCancel: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * ThreadForm Component
 *
 * WHO: Session participants creating threads or posting replies.
 * WHAT: Controlled form that validates, assembles, and delegates payload to parent.
 * WHERE: ThreadList (newThread mode) and ThreadDetail (reply mode).
 * WHY: Centralises form state and validation to avoid duplication.
 *
 * ASYNC PATTERN: onSubmit is awaited; loading state disables inputs during the call.
 */
const ThreadForm: React.FC<ThreadFormProps> = ({ mode, onSubmit, onCancel }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isNewThread = mode === 'newThread';
  const submitLabel = isNewThread ? 'Post Thread' : 'Post Reply';

  /** Validate fields and delegate to parent handler. */
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Client-side validation
    if (isNewThread && !title.trim()) {
      setError('Thread title is required.');
      return;
    }
    if (!content.trim()) {
      setError('Content cannot be empty.');
      return;
    }

    setLoading(true);
    try {
      await onSubmit({ title: isNewThread ? title.trim() : undefined, content: content.trim() });
      // Reset on success
      setTitle('');
      setContent('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      bg="#1a1a22"
      border="1px solid"
      borderColor="#2a2a35"
      borderRadius="md"
      p={5}
    >
      <VStack spacing={4} align="stretch">
        {/* Title field — newThread mode only */}
        {isNewThread && (
          <FormControl isRequired>
            <FormLabel
              color="#e0e0e0"
              fontSize="sm"
              fontFamily="'Cinzel', 'Times New Roman', serif"
            >
              Thread Title
            </FormLabel>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a title…"
              bg="#111116"
              color="#e0e0e0"
              border="1px solid #35889A"
              _placeholder={{ color: '#666' }}
              _focus={{ borderColor: '#35889A', boxShadow: '0 0 0 1px #35889A' }}
              isDisabled={loading}
              fontFamily="'Montserrat', Arial, sans-serif"
            />
          </FormControl>
        )}

        {/* Content textarea */}
        <FormControl isRequired>
          <FormLabel
            color="#e0e0e0"
            fontSize="sm"
            fontFamily="'Cinzel', 'Times New Roman', serif"
          >
            {isNewThread ? 'Opening Post' : 'Reply'}
          </FormLabel>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={isNewThread ? 'Start the discussion…' : 'Write your reply…'}
            bg="#111116"
            color="#e0e0e0"
            border="1px solid #35889A"
            _placeholder={{ color: '#666' }}
            _focus={{ borderColor: '#35889A', boxShadow: '0 0 0 1px #35889A' }}
            isDisabled={loading}
            rows={4}
            fontFamily="'Montserrat', Arial, sans-serif"
            resize="vertical"
          />
        </FormControl>

        {/* Inline error */}
        {error && (
          <Text color="#e57373" fontSize="sm" fontFamily="'Montserrat', Arial, sans-serif">
            {error}
          </Text>
        )}

        {/* Action buttons */}
        <HStack justify="flex-end" spacing={3}>
          <Button
            variant="ghost"
            color="#888"
            onClick={onCancel}
            isDisabled={loading}
            fontFamily="'Montserrat', Arial, sans-serif"
            size="sm"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            bg="#35889A"
            color="white"
            _hover={{ bg: '#2a7080' }}
            isLoading={loading}
            loadingText="Posting…"
            fontFamily="'Montserrat', Arial, sans-serif"
            size="sm"
          >
            {submitLabel}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export { ThreadForm };
